import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import AuthenticatedNavigation from '../../components/navigation/AuthenticatedNavigation';
import MatchCard from './components/MatchCard';
import FilterBar from './components/FilterBar';
import MessageModal from './components/MessageModal';
import RevealRequestModal from './components/RevealRequestModal';
import MeetingScheduler from './components/MeetingScheduler';
import StatsOverview from './components/StatsOverview';
import EmptyState from './components/EmptyState';
import { useNavigate } from 'react-router-dom';

import { matchesService, conversationsService, messagesService, revealRequestsService, interviewsService, savedFiltersService, searchHistoryService } from '../../services/supabaseService';
import { useAuth } from '../../contexts/AuthContext';




const MatchManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [savedFilters, setSavedFilters] = useState([]);
  const [searchHistory, setSearchHistory] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    stage: 'all',
    industry: 'all',
    sort: 'recent'
  });

  const [activeModal, setActiveModal] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);

  const mockMatches = [
    {
      id: 1,
      title: "Senior Engineering Leadership Role",
      description: "Leading a team of 15+ engineers building next-generation cloud infrastructure solutions with focus on scalability and performance optimization.",
      stage: "Meeting Scheduled",
      connectedDate: new Date(Date.now() - 2592000000),
      messageCount: 12,
      industry: "Technology",
      lastMessage: "Looking forward to our meeting next week. I\'ve prepared some technical architecture questions to discuss.",
      lastMessageDate: new Date(Date.now() - 86400000),
      meetingDate: new Date(Date.now() + 604800000)
    },
    {
      id: 2,
      title: "VP of Product Development",
      description: "Strategic product leadership position overseeing multiple product lines and cross-functional teams in fast-growing SaaS company.",
      stage: "Reveal Approved",
      connectedDate: new Date(Date.now() - 1728000000),
      messageCount: 8,
      industry: "Technology",
      lastMessage: "Great to connect! Your experience with product-market fit and go-to-market strategies aligns perfectly with what we\'re looking for.",
      lastMessageDate: new Date(Date.now() - 172800000)
    },
    {
      id: 3,
      title: "Construction Project Director",
      description: "Managing large-scale commercial construction projects with budgets exceeding $50M and coordinating multiple stakeholder relationships.",
      stage: "In Conversation",
      connectedDate: new Date(Date.now() - 1296000000),
      messageCount: 15,
      industry: "Construction",
      lastMessage: "Your experience with complex infrastructure projects is impressive. Can you share more about your approach to risk management?",
      lastMessageDate: new Date(Date.now() - 259200000)
    },
    {
      id: 4,
      title: "Real Estate Development Lead",
      description: "Overseeing mixed-use development projects from acquisition through completion with focus on sustainable urban planning.",
      stage: "Reveal Pending",
      connectedDate: new Date(Date.now() - 864000000),
      messageCount: 6,
      industry: "Real Estate",
      lastMessage: "I\'ve submitted the reveal request. Looking forward to discussing this opportunity in more detail once approved.",
      lastMessageDate: new Date(Date.now() - 345600000)
    },
    {
      id: 5,
      title: "Chief Technology Officer",
      description: "Executive technology leadership role driving digital transformation and innovation strategy for enterprise organization.",
      stage: "Initial Connection",
      connectedDate: new Date(Date.now() - 432000000),
      messageCount: 3,
      industry: "Technology",
      lastMessage: "Thank you for connecting. I\'d love to learn more about your vision for technology leadership in this role.",
      lastMessageDate: new Date(Date.now() - 432000000)
    }
  ];

  // Load matches from Supabase
  useEffect(() => {
    const loadMatches = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await matchesService?.getByUserId(user?.id);
        setMatches(data);
      } catch (err) {
        setError(err?.message);
      } finally {
        setLoading(false);
      }
    };

    loadMatches();
  }, [user]);

  // Load saved filters and search history
  useEffect(() => {
    const loadUserData = async () => {
      if (!user) return;

      try {
        const filters = await savedFiltersService?.getAll(user?.id, 'matches');
        setSavedFilters(filters);

        const history = await searchHistoryService?.getAll(user?.id, 'matches');
        setSearchHistory(history);
      } catch (err) {
        console.error('Error loading user data:', err);
      }
    };

    loadUserData();
  }, [user]);

  const filteredMatches = useMemo(() => {
    return matches;
  }, [matches]);

  const stats = useMemo(() => ({
    totalMatches: matches?.length,
    activeConversations: matches?.filter(m => m?.stage === 'In Conversation')?.length,
    pendingReveals: matches?.filter(m => m?.stage === 'Reveal Pending')?.length,
    scheduledMeetings: matches?.filter(m => m?.stage === 'Meeting Scheduled')?.length
  }), [matches]);

  const handleFilterChange = async (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));

    // Track search history
    if (field === 'search' && value?.trim() && user) {
      try {
        await searchHistoryService?.add(user?.id, value, 'matches');
        const updatedHistory = await searchHistoryService?.getAll(user?.id, 'matches');
        setSearchHistory(updatedHistory);
      } catch (err) {
        console.error('Error saving search:', err);
      }
    }
  };

  const handleSaveFilter = async (name, filterValues) => {
    if (!user) return;

    try {
      await savedFiltersService?.create(user?.id, name, filterValues, 'matches');
      const updated = await savedFiltersService?.getAll(user?.id, 'matches');
      setSavedFilters(updated);
    } catch (err) {
      console.error('Error saving filter:', err);
    }
  };

  const handleApplyFilter = (filterData) => {
    setFilters(filterData);
  };

  const handleDeleteFilter = async (filterId) => {
    try {
      await savedFiltersService?.delete(filterId);
      const updated = await savedFiltersService?.getAll(user?.id, 'matches');
      setSavedFilters(updated);
    } catch (err) {
      console.error('Error deleting filter:', err);
    }
  };

  const handleApplySearch = (search) => {
    setFilters((prev) => ({ ...prev, search }));
  };

  const handleClearHistory = async () => {
    if (!user) return;

    try {
      await searchHistoryService?.clear(user?.id, 'matches');
      setSearchHistory([]);
    } catch (err) {
      console.error('Error clearing history:', err);
    }
  };

  const handleBulkMessage = () => {
    if (selectedIds?.length === 0) return;
    // Open message modal for first selected match
    const firstMatch = matches?.find(m => m?.id === selectedIds?.[0]);
    setSelectedMatch(firstMatch);
    setActiveModal('message');
  };

  const handleBulkReveal = async () => {
    if (selectedIds?.length === 0 || !user) return;

    try {
      for (const matchId of selectedIds) {
        await revealRequestsService?.create({
          matchId: matchId,
          message: 'Bulk reveal request'
        });
      }

      // Refresh matches
      const updatedMatches = await matchesService?.getByUserId(user?.id);
      setMatches(updatedMatches);
      setSelectedIds([]);
    } catch (err) {
      console.error('Error sending reveal requests:', err);
    }
  };

  const handleBulkArchive = () => {
    if (selectedIds?.length === 0) return;
    
    selectedIds?.forEach(matchId => {
      matchesService?.update(matchId, { archived: true });
    });
    
    // Refresh matches
    const updatedMatches = matchesService?.getByUserId(user?.id);
    setMatches(updatedMatches);
    setSelectedIds([]);
  };

  const handleMessageSend = async (matchId, message) => {
    if (!user) return;

    try {
      const match = matches?.find(m => m?.id === matchId);
      if (!match) return;

      // Get or create conversation
      let conversation = await conversationsService?.getByUserId(user?.id);
      conversation = conversation?.find(c => c?.matchId === matchId);

      if (!conversation) {
        // Create new conversation
        const otherUserId = match?.candidate?.userId === user?.id 
          ? match?.employer?.userId 
          : match?.candidate?.userId;

        conversation = await conversationsService?.create({
          matchId: matchId,
          participant1Id: user?.id,
          participant2Id: otherUserId
        });
      }

      // Send message
      await messagesService?.create({
        conversationId: conversation?.id,
        content: message
      });

      handleCloseModal();

      // Refresh matches
      const updatedMatches = await matchesService?.getByUserId(user?.id);
      setMatches(updatedMatches);
    } catch (err) {
      console.error('Error sending message:', err);
    }
  };

  const handleRevealSubmit = async (matchId) => {
    if (!user) return;

    try {
      await revealRequestsService?.create({
        matchId: matchId,
        message: 'Request to reveal identity'
      });

      handleCloseModal();
    } catch (err) {
      console.error('Error submitting reveal request:', err);
    }
  };

  const handleMeetingSchedule = async (matchId, meetingData) => {
    if (!user) return;

    try {
      await interviewsService?.create({
        matchId: matchId,
        scheduledAt: meetingData?.date,
        durationMinutes: 60,
        meetingLink: meetingData?.meetingLink,
        notes: meetingData?.notes
      });

      handleCloseModal();
    } catch (err) {
      console.error('Error scheduling meeting:', err);
    }
  };

  const handleCloseModal = () => {
    setActiveModal(null);
    setSelectedMatch(null);
  };

  const handleSendMessage = (match) => {
    setSelectedMatch(match);
    setActiveModal('message');
  };

  const handleRequestReveal = (match) => {
    setSelectedMatch(match);
    setActiveModal('reveal');
  };

  const handleScheduleMeeting = (match) => {
    setSelectedMatch(match);
    setActiveModal('meeting');
  };

  const filterActive = filters?.search || filters?.stage !== 'all' || filters?.industry !== 'all';

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
        <AuthenticatedNavigation userRole="candidate" matchCount={0} />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-white">
            <p>Loading matches...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
        <AuthenticatedNavigation userRole="candidate" matchCount={0} />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-red-400">
            <p>Error loading matches: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Match Management | Executive Connect</title>
      </Helmet>
      <AuthenticatedNavigation />
      <div className="min-h-screen bg-white lg:pl-60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Header */}
          <div className="mb-4">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Match Management</h1>
            <p className="text-sm text-gray-600">Manage your connections and opportunities</p>
          </div>

          {/* Stats Overview */}
          <div className="mb-4">
            <StatsOverview stats={stats} />
          </div>

          {/* Filter Bar */}
          <div className="mb-4">
            <FilterBar
              filters={filters}
              onFilterChange={handleFilterChange}
              onSearch={handleApplySearch}
              searchHistory={searchHistory}
              savedFilters={savedFilters}
              onSaveFilter={handleSaveFilter}
              onApplyFilter={handleApplyFilter}
              onDeleteFilter={handleDeleteFilter}
              onClearHistory={handleClearHistory}
              selectedCount={selectedIds?.length}
              onBulkMessage={handleBulkMessage}
              onBulkReveal={handleBulkReveal}
              onBulkArchive={handleBulkArchive}
              onClearSelection={() => setSelectedIds([])}
            />
          </div>

          {filteredMatches?.length === 0 ? (
            <EmptyState 
              filters={filters}
              onExploreOpportunities={() => {
                // Reset filters to show all opportunities
                setFilters({
                  search: '',
                  stage: 'all',
                  industry: 'all',
                  sort: 'recent'
                });
              }}
            />
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {filteredMatches?.map(match => (
                <MatchCard
                  key={match?.id}
                  match={match}
                  onSendMessage={handleSendMessage}
                  onRequestReveal={handleRequestReveal}
                  onScheduleMeeting={handleScheduleMeeting}
                  onArchive={async (matchId) => {
                    try {
                      await matchesService?.update(matchId, { archived: true });
                      const updatedMatches = await matchesService?.getByUserId(user?.id);
                      setMatches(updatedMatches);
                    } catch (err) {
                      console.error('Error archiving match:', err);
                    }
                  }}
                  isSelected={selectedIds?.includes(match?.id)}
                  onSelect={(matchId) => {
                    setSelectedIds(prev => 
                      prev?.includes(matchId) 
                        ? prev?.filter(id => id !== matchId)
                        : [...prev, matchId]
                    );
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {activeModal === 'message' && selectedMatch && (
        <MessageModal
          match={selectedMatch}
          onClose={handleCloseModal}
          onSend={handleMessageSend}
        />
      )}
      {activeModal === 'reveal' && selectedMatch && (
        <RevealRequestModal
          match={selectedMatch}
          onClose={handleCloseModal}
          onSubmit={handleRevealSubmit}
        />
      )}
      {activeModal === 'meeting' && selectedMatch && (
        <MeetingScheduler
          match={selectedMatch}
          onClose={handleCloseModal}
          onSchedule={handleMeetingSchedule}
        />
      )}
    </>
  );
};

export default MatchManagement;